import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  IconButton,
  InputAdornment,
  Checkbox,
  Divider,
  CircularProgress,
} from "@mui/material";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import api from "../api";
import { useToast } from "./ToastProvider";
import { HEADING_FONT } from "../theme/fonts";
import { COLORS } from "../theme/tokens";
import {
  PERMISSION_MODULES,
  PERMISSION_ACTIONS,
  emptyPermissions,
  normalizePermissions,
} from "../constants/modules";

// Flat spec design tokens (light-only) — same key names the old glass theme used.
const colors = {
  text: COLORS.ink,
  textMuted: COLORS.muted,
  textFaint: COLORS.faint,
  cardBg: COLORS.surface,
  cardBorder: COLORS.hairline,
  cardBorderHover: COLORS.borderHover,
  hover: COLORS.tile,
  divider: COLORS.hairline,
};

function SectionHeader({ icon, title }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "10px",
          background: "rgba(59, 130, 246, 0.12)",
          color: COLORS.primary,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </Box>
      <Typography
        sx={{
          fontFamily: HEADING_FONT,
          fontSize: 14,
          fontWeight: 700,
          color: colors.text,
          letterSpacing: "-0.2px",
          lineHeight: 1.2,
          mt: "2px",
        }}
      >
        {title}
      </Typography>
    </Stack>
  );
}

const ROLE_OPTIONS = [
  {
    value: "admin",
    label: "Admin",
    description: "Full access · manage users, data & settings",
    icon: <AdminPanelSettingsOutlinedIcon />,
    accent: COLORS.primary,
    bg: "rgba(59, 130, 246, 0.12)",
    hoverBg: "rgba(59, 130, 246, 0.18)",
  },
  {
    value: "user",
    label: "User",
    description: "Limited access · only the modules you grant",
    icon: <PersonOutlineOutlinedIcon />,
    accent: "#f59e0b",
    bg: "rgba(245, 158, 11, 0.12)",
    hoverBg: "rgba(245, 158, 11, 0.18)",
  },
];

// Reusable Create/Edit User form body (spec §5.4): Account Credentials, Role
// (Admin/User cards), and the Module Access permission matrix. Used by the
// AddUserModal and the thin /users/new, /users/:id/edit page wrappers.
//
// Props:
//   userId    — null/undefined = create (POST); an id = edit (loads + PUT).
//   onSuccess — called after a successful save (receives { isEdit }).
//   onCancel  — called when the user clicks Cancel.
function UserFormBody({ userId = null, onSuccess, onCancel }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const isEdit = userId != null;

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("user");
  const [permissions, setPermissions] = useState(emptyPermissions());
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  // Edit mode: load the existing user once.
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const user = await api.get(`/users/${userId}`);
        if (cancelled) return;
        setUsername(user.username || "");
        setRole(user.role || "user");
        setPermissions(normalizePermissions(user.permissions));
      } catch (err) {
        if (!cancelled) toast.error(err.message || "Failed to load user");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, isEdit, toast]);

  // --- Permission grid helpers ---
  function togglePermission(moduleKey, action) {
    setPermissions((prev) => {
      const grid = normalizePermissions(prev);
      const next = !grid[moduleKey][action];
      grid[moduleKey] = { ...grid[moduleKey], [action]: next };
      // Turning on any action implies the user can at least view the module.
      if (next && action !== "view") grid[moduleKey].view = true;
      // Removing view removes everything else (can't act on what you can't see).
      if (!next && action === "view") {
        grid[moduleKey] = { view: false, add: false, edit: false, delete: false };
      }
      return grid;
    });
  }

  function toggleModuleAll(moduleKey, value) {
    setPermissions((prev) => {
      const grid = normalizePermissions(prev);
      grid[moduleKey] = { view: value, add: value, edit: value, delete: value };
      return grid;
    });
  }

  function toggleEverything(value) {
    setPermissions(() => {
      const grid = emptyPermissions();
      if (value) {
        for (const m of PERMISSION_MODULES) {
          grid[m.key] = { view: true, add: true, edit: true, delete: true };
        }
      }
      return grid;
    });
  }

  async function handleSave() {
    if (!username.trim()) {
      toast.error("Username is required");
      return;
    }
    if (!isEdit && !password) {
      toast.error("Password is required for new users");
      return;
    }
    setSaving(true);
    try {
      // Admins get full access implicitly, so we store null permissions for
      // them; only "user" accounts carry a permission map.
      const perms = role === "user" ? normalizePermissions(permissions) : null;
      if (!isEdit) {
        await api.post("/users", {
          username: username.trim(),
          password,
          role,
          permissions: perms,
        });
      } else {
        const payload = { username: username.trim(), role, permissions: perms };
        if (password) payload.password = password;
        await api.put(`/users/${userId}`, payload);
      }
      queryClient.invalidateQueries({ queryKey: ["users"] });
      onSuccess?.({ isEdit });
    } catch (err) {
      toast.error(err.message || "Failed to save user");
      setSaving(false);
    }
  }

  const labelSx = {
    display: "block",
    fontSize: 12,
    fontWeight: 600,
    color: colors.textMuted,
    mb: 0.75,
  };
  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      background: colors.cardBg,
      color: colors.text,
      borderRadius: "10px",
      "& fieldset": { borderColor: colors.cardBorder },
      "&:hover fieldset": { borderColor: colors.cardBorderHover },
      "&.Mui-focused fieldset": { borderColor: COLORS.primary },
    },
    "& input": {
      color: colors.text,
      "&::placeholder": { color: colors.textFaint, opacity: 1 },
      "&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus": {
        WebkitBoxShadow: `0 0 0 1000px ${colors.cardBg} inset`,
        WebkitTextFillColor: colors.text,
        caretColor: colors.text,
        transition: "background-color 9999s ease-in-out 0s",
      },
    },
  };
  // Flat white card (no backdrop blur).
  const cardSx = {
    background: "#ffffff",
    border: `1px solid ${colors.cardBorder}`,
    borderRadius: "16px",
    boxShadow: "0 1px 3px rgba(16, 24, 40, 0.05)",
    p: 3,
  };
  const gridSx = {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
    gap: 2,
  };

  const everythingOn =
    role === "user" &&
    PERMISSION_MODULES.every((m) => {
      const r = normalizePermissions(permissions)[m.key];
      return r.view && r.add && r.edit && r.delete;
    });

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress sx={{ color: COLORS.primary }} />
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      {/* Account credentials */}
      <Box sx={cardSx}>
        <SectionHeader
          icon={<BadgeOutlinedIcon sx={{ fontSize: 20 }} />}
          title="Account Credentials"
        />
        <Divider sx={{ borderColor: colors.divider, mb: 2.5 }} />
        <Box sx={gridSx}>
          <Box>
            <Typography component="label" sx={labelSx}>
              Username
            </Typography>
            <TextField
              fullWidth
              placeholder="e.g. john"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="off"
              sx={fieldSx}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonOutlineOutlinedIcon
                        sx={{ fontSize: 18, color: colors.textMuted }}
                      />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>
          <Box>
            <Typography component="label" sx={labelSx}>
              {isEdit ? "New Password" : "Password"}
            </Typography>
            <TextField
              fullWidth
              placeholder={
                isEdit ? "Leave blank to keep current" : "Set a password"
              }
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              sx={fieldSx}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon
                        sx={{ fontSize: 18, color: colors.textMuted }}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((s) => !s)}
                        edge="end"
                        size="small"
                        sx={{ color: colors.textMuted }}
                      >
                        {showPassword ? (
                          <VisibilityOffOutlinedIcon sx={{ fontSize: 18 }} />
                        ) : (
                          <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Role */}
      <Box sx={cardSx}>
        <SectionHeader
          icon={<VerifiedUserOutlinedIcon sx={{ fontSize: 20 }} />}
          title="Role"
        />
        <Divider sx={{ borderColor: colors.divider, mb: 2.5 }} />
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          {ROLE_OPTIONS.map((opt) => {
            const selected = role === opt.value;
            return (
              <Box
                key={opt.value}
                component="button"
                type="button"
                onClick={() => setRole(opt.value)}
                sx={{
                  flex: 1,
                  textAlign: "left",
                  cursor: "pointer",
                  p: 2,
                  borderRadius: "10px",
                  border: `1px solid ${
                    selected ? opt.accent : colors.cardBorder
                  }`,
                  background: selected ? opt.bg : colors.cardBg,
                  transition: "all 0.15s ease",
                  position: "relative",
                  "&:hover": {
                    background: selected ? opt.hoverBg : colors.hover,
                    borderColor: selected ? opt.accent : colors.cardBorderHover,
                  },
                }}
              >
                {selected && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 10,
                      right: 10,
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      background: opt.accent,
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    ✓
                  </Box>
                )}
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: "10px",
                      background: selected ? opt.accent : colors.hover,
                      color: selected ? "#fff" : colors.textMuted,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      "& .MuiSvgIcon-root": { fontSize: 20 },
                    }}
                  >
                    {opt.icon}
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: selected ? opt.accent : colors.text,
                      }}
                    >
                      {opt.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: colors.textMuted,
                        lineHeight: 1.4,
                      }}
                    >
                      {opt.description}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            );
          })}
        </Stack>
      </Box>

      {/* Module access — only for the "user" role */}
      {role === "user" && (
        <Box sx={cardSx}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <SectionHeader
              icon={<ShieldOutlinedIcon sx={{ fontSize: 20 }} />}
              title="Module Access"
            />
            <Button
              onClick={() => toggleEverything(!everythingOn)}
              size="small"
              sx={{
                flexShrink: 0,
                textTransform: "none",
                fontWeight: 600,
                fontSize: 12.5,
                mb: 2,
                color: everythingOn ? colors.textMuted : COLORS.primary,
                borderRadius: "10px",
                px: 1.5,
                "&:hover": { background: colors.hover },
              }}
            >
              {everythingOn ? "Clear all" : "Grant everything"}
            </Button>
          </Stack>
          <Divider sx={{ borderColor: colors.divider, mb: 2.5 }} />

          <Box
            sx={{
              border: `1px solid ${colors.cardBorder}`,
              borderRadius: "10px",
              overflow: "hidden",
            }}
          >
            {/* Horizontal scroll so the fixed action columns never clip on
                narrow screens / inside the dialog. */}
            <Box sx={{ overflowX: "auto" }}>
              <Box sx={{ minWidth: 360 }}>
            {/* Header row */}
            <Stack
              direction="row"
              alignItems="center"
              sx={{
                px: 2,
                py: 1.25,
                background: colors.hover,
                borderBottom: `1px solid ${colors.divider}`,
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 11,
                  fontWeight: 700,
                  color: colors.textMuted,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Module
              </Box>
              {PERMISSION_ACTIONS.map((a) => (
                <Box
                  key={a.key}
                  sx={{
                    width: { xs: 56, sm: 68 },
                    textAlign: "center",
                    fontSize: 11,
                    fontWeight: 700,
                    color: colors.textMuted,
                    textTransform: "uppercase",
                  }}
                >
                  {a.label}
                </Box>
              ))}
              <Box
                sx={{
                  width: { xs: 56, sm: 68 },
                  textAlign: "center",
                  fontSize: 11,
                  fontWeight: 700,
                  color: colors.textMuted,
                  textTransform: "uppercase",
                }}
              >
                All
              </Box>
            </Stack>

            {PERMISSION_MODULES.map((m, idx) => {
              const grid = normalizePermissions(permissions);
              const row = grid[m.key];
              const allOn = row.view && row.add && row.edit && row.delete;
              return (
                <Stack
                  key={m.key}
                  direction="row"
                  alignItems="center"
                  sx={{
                    px: 2,
                    py: 0.5,
                    borderBottom:
                      idx < PERMISSION_MODULES.length - 1
                        ? `1px solid ${colors.divider}`
                        : "none",
                    transition: "background 0.15s ease",
                    "&:hover": { background: colors.hover },
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: 13.5,
                      fontWeight: 600,
                      color: colors.text,
                    }}
                  >
                    {m.label}
                  </Box>
                  {PERMISSION_ACTIONS.map((a) => (
                    <Box
                      key={a.key}
                      sx={{ width: { xs: 56, sm: 68 }, textAlign: "center" }}
                    >
                      <Checkbox
                        size="small"
                        checked={!!row[a.key]}
                        onChange={() => togglePermission(m.key, a.key)}
                        sx={{
                          color: colors.textMuted,
                          "&.Mui-checked": { color: COLORS.primary },
                        }}
                      />
                    </Box>
                  ))}
                  <Box sx={{ width: { xs: 56, sm: 68 }, textAlign: "center" }}>
                    <Checkbox
                      size="small"
                      checked={allOn}
                      onChange={(e) => toggleModuleAll(m.key, e.target.checked)}
                      sx={{
                        color: colors.textMuted,
                        "&.Mui-checked": { color: "#16A34A" },
                      }}
                    />
                  </Box>
                </Stack>
              );
            })}
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* Actions */}
      <Stack
        direction="row"
        spacing={1.5}
        justifyContent="flex-end"
        sx={{ pt: 1, borderTop: `1px solid ${colors.divider}` }}
      >
        <Button
          onClick={onCancel}
          disabled={saving}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            px: 3,
            color: colors.textMuted,
            "&:hover": { background: colors.hover },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            px: 3.5,
            py: 1,
            color: "#fff",
            background: COLORS.primary,
            boxShadow: "0 4px 14px rgba(59, 130, 246, 0.35)",
            "&:hover": { boxShadow: "0 8px 22px rgba(59, 130, 246, 0.45)" },
            "&.Mui-disabled": {
              color: "rgba(255,255,255,0.6)",
              background: colors.cardBorder,
            },
          }}
        >
          {saving ? "Saving…" : isEdit ? "Update User" : "Create User"}
        </Button>
      </Stack>
    </Stack>
  );
}

export default UserFormBody;
